import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  InputAdornment,
  Divider,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import EmailIcon from '@mui/icons-material/Email';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: 6 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const MobileInput = ({ onSubmit, onPasskeyLogin, onGoogleLogin, onEmailSignup, loading }) => {
  const [mobile, setMobile] = useState('');
  const [countryCode, setCountryCode] = useState('+44');
  const [error, setError] = useState('');
  const [passkeyPhone, setPasskeyPhone] = useState(null);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('passkey_phone');
    if (!stored || !window.PublicKeyCredential) return;
    window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
      .then(available => {
        if (available) {
          setPasskeyAvailable(true);
          setPasskeyPhone(stored);
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!mobile || mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    onSubmit(mobile, countryCode);
  };

  const maskedPhone = passkeyPhone ? `••••••${passkeyPhone.slice(-4)}` : '';

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      <Typography variant="h5" gutterBottom align="center">
        Welcome to CircleMoments
      </Typography>

      {passkeyAvailable && passkeyPhone && (
        <>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FingerprintIcon />}
            onClick={() => onPasskeyLogin(passkeyPhone)}
            disabled={loading}
            sx={{ py: 1.5, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' }}
          >
            Sign in with Fingerprint&nbsp;
            <Typography component="span" variant="caption" sx={{ opacity: 0.85, ml: 0.5 }}>
              ({maskedPhone})
            </Typography>
          </Button>
          <Divider><Typography variant="caption" color="text.secondary">or continue with</Typography></Divider>
        </>
      )}

      {/* Google Sign-In */}
      <Button
        variant="outlined"
        size="large"
        fullWidth
        onClick={onGoogleLogin}
        disabled={loading}
        sx={{ py: 1.5, borderColor: '#dadce0', color: 'text.primary', '&:hover': { borderColor: '#bbb', bgcolor: 'grey.50' } }}
      >
        <GoogleIcon />
        Continue with Google
      </Button>

      <Divider><Typography variant="caption" color="text.secondary">or use phone number</Typography></Divider>

      <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 1.5, alignItems: 'start' }}>
        <FormControl fullWidth disabled={loading}>
          <Select
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            sx={{ height: 56 }}
          >
            <MenuItem value="+44">+44</MenuItem>
            <MenuItem value="+91">+91</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Mobile Number"
          variant="outlined"
          value={mobile}
          onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
          error={!!error}
          helperText={error}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Get Started'}
      </Button>

      <Divider sx={{ my: 0 }} />

      {/* Email Sign-Up */}
      <Button
        variant="text"
        size="medium"
        fullWidth
        startIcon={<EmailIcon />}
        onClick={onEmailSignup}
        disabled={loading}
        sx={{ color: 'text.secondary' }}
      >
        Sign up / Sign in with Email
      </Button>
    </Box>
  );
};

export default MobileInput;
